/**
 * Quote-api base + admin headers for dashboard.
 * Production: https://pipeup.ir/quote-api + dashboard JWT (x-auth).
 * Isolated: 127.0.0.1:4030 + x-quote-admin: 1.
 */

import { storage } from "./storage";

const STORAGE_KEY = "pipeup_quote_api_base";

const UI_PORT_TO_API_PORT = {
  "18011": "18030",
  "18012": "18030",
  "3011": "4030",
  "3012": "4030",
};

const API_PORT_TO_STOREFRONT_PORT = {
  "18030": "18011",
  "4030": "3011",
};

function hostFromLocation() {
  if (typeof window === "undefined") return "127.0.0.1";
  const h = window.location.hostname;
  return h === "localhost" ? "127.0.0.1" : h;
}

function isProductionHost(hostname) {
  return (
    hostname === "pipeup.ir" ||
    hostname === "www.pipeup.ir" ||
    hostname === "dashboard.pipeup.ir"
  );
}

export function resolveQuoteApiBase() {
  if (typeof window === "undefined") {
    return process.env.REACT_APP_QUOTE_API_BASE || "http://127.0.0.1:4030";
  }

  if (process.env.REACT_APP_QUOTE_API_BASE) {
    return String(process.env.REACT_APP_QUOTE_API_BASE).replace(/\/$/, "");
  }

  const hostname = window.location.hostname;
  if (isProductionHost(hostname)) {
    return "https://pipeup.ir/quote-api";
  }

  try {
    const q = new URLSearchParams(window.location.search).get("quoteApi");
    if (q && /^https?:\/\/127\.0\.0\.1(?::\d+)?\/?$/i.test(q.trim())) {
      const cleaned = q.trim().replace(/\/$/, "");
      window.localStorage.setItem(STORAGE_KEY, cleaned);
      return cleaned;
    }
  } catch (_) {
    /* ignore */
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && /^https?:\/\/127\.0\.0\.1(?::\d+)?$/i.test(stored)) {
      return stored;
    }
  } catch (_) {
    /* ignore */
  }

  const uiPort = String(window.location.port || "");
  const apiPort = UI_PORT_TO_API_PORT[uiPort] || "4030";
  return `http://${hostFromLocation()}:${apiPort}`;
}

export const QUOTE_API_BASE =
  typeof window !== "undefined"
    ? resolveQuoteApiBase()
    : process.env.REACT_APP_QUOTE_API_BASE || "http://127.0.0.1:4030";

export function getQuoteApiBase() {
  return resolveQuoteApiBase();
}

/**
 * Async admin headers — production uses dashboard x-auth JWT.
 */
export async function quoteAdminHeaders() {
  const headers = { "Content-Type": "application/json" };
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";
  const forceDashboard =
    process.env.REACT_APP_QUOTE_AUTH_MODE === "dashboard" ||
    isProductionHost(hostname);

  if (forceDashboard) {
    try {
      const token = await storage.load("token");
      if (token) {
        headers["x-auth"] = typeof token === "string" ? token : String(token);
      }
    } catch (_) {
      /* ignore */
    }
    headers["x-admin-id"] = "dashboard";
    return headers;
  }

  // Isolated preview only
  headers["x-quote-admin"] = "1";
  headers["x-admin-id"] = "isolated-dashboard";
  return headers;
}

export function rewriteCustomerLink(url) {
  if (!url || typeof window === "undefined") return url;
  try {
    if (isProductionHost(window.location.hostname)) {
      return String(url)
        .replace(/https?:\/\/127\.0\.0\.1:\d+/g, "https://pipeup.ir")
        .replace(/http:\/\/localhost:\d+/g, "https://pipeup.ir");
    }
    const apiBase = resolveQuoteApiBase();
    const apiPort = new URL(apiBase).port || "4030";
    const storePort = API_PORT_TO_STOREFRONT_PORT[apiPort] || "3011";
    let out = String(url);
    out = out.replace(/127\.0\.0\.1:4030/g, `127.0.0.1:${apiPort}`);
    out = out.replace(/127\.0\.0\.1:3011/g, `127.0.0.1:${storePort}`);
    out = out.replace(
      new RegExp(`http://127\\.0\\.0\\.1:${apiPort}/q/`, "g"),
      `http://127.0.0.1:${storePort}/q/`
    );
    return out;
  } catch (_) {
    return url;
  }
}
